import { AloftPrice } from '@aloft/toolkit-shared';

import { CompSelectionProperty, CompSelectionState } from '../types.ts';

export const getSelectedIDs = (
  rowSelection: CompSelectionState,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): string[] => {
  if (isInspectifyOrder && hasArvOption) {
    return Object.entries(rowSelection)
      .filter(([, value]) => value.aiv || value.arv)
      .map(([key]) => key);
  }

  return Object.keys(rowSelection);
};

const averageSalePrice = (prices: AloftPrice[]): AloftPrice | undefined => {
  if (prices.length === 0) return undefined;
  return AloftPrice.add(...prices).div(prices.length);
};

type SelectedCompStats = {
  selectedCount: number;
  aivAvgSalePrice?: AloftPrice;
  arvAvgSalePrice?: AloftPrice;
  difference?: AloftPrice;
};

export const getSelectedCompStats = ({
  rowSelection,
  properties,
  isInspectifyOrder,
  hasArvOption,
}: {
  rowSelection: CompSelectionState;
  properties: CompSelectionProperty[];
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
}): SelectedCompStats => {
  const selectedIDs = getSelectedIDs(rowSelection, isInspectifyOrder, hasArvOption).filter(
    (id) => id !== 'subject',
  );

  if (!hasArvOption) return { selectedCount: selectedIDs.length };

  const propertiesById = new Map(properties.map((property) => [property.id, property]));

  const aivPrices: AloftPrice[] = [];
  const arvPrices: AloftPrice[] = [];

  for (const id of selectedIDs) {
    const selection = rowSelection[id];
    const salePrice = propertiesById.get(id)?.salePrice;
    if (!salePrice || !selection) continue;
    if (selection.aiv) aivPrices.push(salePrice);
    if (selection.arv) arvPrices.push(salePrice);
  }

  const aivAvgSalePrice = averageSalePrice(aivPrices);
  const arvAvgSalePrice = averageSalePrice(arvPrices);
  const difference =
    aivAvgSalePrice && arvAvgSalePrice ? arvAvgSalePrice.sub(aivAvgSalePrice) : undefined;

  return {
    selectedCount: selectedIDs.length,
    aivAvgSalePrice,
    arvAvgSalePrice,
    difference,
  };
};
